import api from './api-client';

// `inventory-documents` + `receipts` (and, from Task 28, the `/inventory/*`
// reads) are their own vertical, so they live beside `catalog.js` rather than
// growing `resources.js`.
export const inventoryDocumentsApi = {
  list: (params) => api.get('/inventory-documents', { params }).then((r) => r.data),
  get: (id) => api.get(`/inventory-documents/${id}`).then((r) => r.data),
  // Line writes answer with the line alone, so every caller re-reads the
  // document afterwards to pick up its bumped `version`. `replaceLines` is
  // the exception — it answers with the full updated document already,
  // which is the whole point of the endpoint.
  createLine: (id, data) => api.post(`/inventory-documents/${id}/lines`, data).then((r) => r.data),
  updateLine: (id, lineId, data) => api.patch(`/inventory-documents/${id}/lines/${lineId}`, data).then((r) => r.data),
  removeLine: (id, lineId, expectedVersion) => api.delete(`/inventory-documents/${id}/lines/${lineId}`, { params: { expected_version: expectedVersion } }),
  replaceLines: (id, data) => api.put(`/inventory-documents/${id}/lines`, data).then((r) => r.data),
  // Answers with a print snapshot, not a document: the assigned `document_no`
  // and the new status only arrive on the next `get`.
  print: (id) => api.post(`/inventory-documents/${id}/print`).then((r) => r.data),
  acknowledgeSignatures: (id, signatures) => api.post(`/inventory-documents/${id}/acknowledge-signatures`, { signatures, advance: true }).then((r) => r.data),
  confirm: (id, expectedVersion, idempotencyKey) => api.post(`/inventory-documents/${id}/confirm`, { expected_version: expectedVersion }, { headers: { 'Idempotency-Key': idempotencyKey } }).then((r) => r.data),
  cancel: (id, reason) => api.post(`/inventory-documents/${id}/cancel`, { reason }).then((r) => r.data),
  // Header edit, DRAFT only. Today it carries exactly one field — the phiếu
  // điều chỉnh's mandatory "Lý do điều chỉnh" — because every other header
  // column is issued by the server or fixed when the chứng từ is raised.
  updateReason: (id, discrepancyReason) => api.patch(`/inventory-documents/${id}`, { discrepancy_reason: discrepancyReason }).then((r) => r.data),
};

export const receiptsApi = { create: (data) => api.post('/receipts', data).then((r) => r.data) };

// A phiếu xuất is created whole — header, mọi đơn hàng and mọi dòng in one
// request — because its dòng name vật tư and khu that already exist, so there is
// nothing to fill in line by line the way a phiếu nhập is. From here it runs the
// ordinary print → ký → confirm lifecycle through `inventoryDocumentsApi`.
//
// Payload nhóm theo đơn: `orders: [{order_code, order_kind, lines}]`. Một phiếu
// phục vụ nhiều đơn hàng, và mã đơn nằm trên *dòng* chứ không trên đầu phiếu —
// `order_kind` quyết định tiêu hao của một số lượng tính vào sản xuất thường hay
// tính bù lỗi cho tổ, nên nó phải đi cùng số lượng đó.
//
// `order_kind` bắt buộc trên mỗi khối và là **lời khai của người lập phiếu**: hệ
// thống quản lý đơn hàng không phân loại đơn, nên không có giá trị nguồn nào để
// rơi về và cũng không có "ghi đè" nào để ghi nhật ký. Mọi con số dựng trên nó
// là con số dựng trên điều người lập phiếu đã chọn.
// `replace` viết lại **cả** nội dung một phiếu nháp, không vá từng dòng: khu,
// số lượng và bộ cuộn của một dòng là kết quả của một lượt chia khu, nên gửi
// một phần trong số đó là gửi một kế hoạch chỉ đúng một nửa. Payload giống hệt
// `create` cộng thêm `version`, và cố tình **không** có `owning_warehouse_id` —
// kho quyết định mọi khu đã khai, nên đổi nó là lập một phiếu khác.
export const issuesApi = {
  create: (data) => api.post('/issues', data).then((r) => r.data),
  replace: (id, data) => api.put(`/issues/${id}`, data).then((r) => r.data),
};

// A read-only window onto the order system, only ever used to fill the đơn
// hàng dropdown. Answers `{items:[{code,name,kind,customer}]}` — not a
// paginated envelope, because the warehouse does not hold that collection and
// cannot page through it. Only `code` is ever stored on a chứng từ.
export const ordersApi = { list: (params) => api.get('/orders', { params }).then((r) => r.data.items) };

// design §6 of the opening-balance import. A batch never writes tồn: the furthest
// it goes is `create-document`, which mints an `OPENING` chứng từ in DRAFT — the
// print → ký → confirm half of its life belongs to `inventoryDocumentsApi`, like
// every other chứng từ, so nothing here answers for it.
export const openingImportsApi = {
  list: (params) => api.get('/opening-imports', { params }).then((r) => r.data),
  // `get` and `create` both answer with the batch *plus* `duplicate_file_warning`
  // — a hint that another live batch of this kho already holds the same file,
  // never a refusal.
  get: (id) => api.get(`/opening-imports/${id}`).then((r) => r.data),
  // Multipart: `file`, `warehouse_id`, `document_date`. Hand it a `FormData` and
  // let the browser set the boundary; naming a Content-Type here breaks it.
  create: (formData) => api.post('/opening-imports', formData).then((r) => r.data),
  // Re-runnable by design — "sửa danh mục rồi bấm Kiểm tra lại" — so it takes no
  // `expected_version`, and it answers 200 even when every dòng is wrong: a row
  // error is data (`invalid_rows`), not an HTTP failure. It does bump the
  // batch's `version`, so re-read the batch before `createDocument`.
  validate: (id, idempotencyKey) => api.post(`/opening-imports/${id}/validate`, {}, { headers: { 'Idempotency-Key': idempotencyKey } }).then((r) => r.data),
  rows: (id, params) => api.get(`/opening-imports/${id}/rows`, { params }).then((r) => r.data),
  // The INVALID rows rebuilt as a CSV to fix and re-upload. Fetched through the
  // client rather than linked to, because a bare `<a href>` carries no
  // Authorization header; the bytes already hold their own BOM and delimiter, so
  // they are taken as a blob and never parsed.
  errorsCsv: (id) => api.get(`/opening-imports/${id}/errors.csv`, { responseType: 'blob' }).then((r) => r.data),
  // Answers with an envelope, not a bare chứng từ: `{ batch, document }`, where
  // `document` is the very shape `GET /inventory-documents/{id}` returns.
  createDocument: (id, expectedVersion, idempotencyKey) => api.post(`/opening-imports/${id}/create-document`, { expected_version: expectedVersion }, { headers: { 'Idempotency-Key': idempotencyKey } }).then((r) => r.data),
  // No reason field: a batch cancel has none. Once a document exists the batch
  // stays DOCUMENT_CREATED and the way back out is to cancel that document.
  cancel: (id) => api.post(`/opening-imports/${id}/cancel`).then((r) => r.data),
};

// design §7.3 reads. `/inventory/balances` filters by id only (`material_id`,
// `material_type_id`, `kind`) and has no `search` param, so
// nothing here takes one;
// the balance row itself carries no material, which is why `stockUnit` exists
// beside it.
export const inventoryApi = {
  balances: (params) => api.get('/inventory/balances', { params }).then((r) => r.data),
  // The same rows totalled per (vật tư, ĐVT). Takes the same filters, so the
  // totals always describe exactly the detail rows `balances` would return; its
  // sort keys are its own though (`material_code`/`physical_quantity`).
  balanceSummary: (params) => api.get('/inventory/balances/summary', { params }).then((r) => r.data),
  // The same rows gộp theo **đợt nhập** — một dòng phiếu nhập, tổng của mọi đơn vị
  // nó sinh ra. Tầng giữa của bảng Tồn kho: gộp ở máy chủ vì phân trang ở máy chủ,
  // nên cộng trên trang đã về chỉ cộng được phần đang thấy. Đợt nhiều đơn vị mang
  // sẵn `units` nên xoè ra không tốn thêm lượt gọi nào.
  // How many vật tư each choice of one filter control would leave, counted off
  // the very same query the table runs — pass the filters that are in force
  // *minus* the one being counted, or every option but the ticked one answers 0.
  // Answers a bare list (`[{value,count}]`), not a paginated envelope: a facet is
  // already one row per choice.
  balanceSummaryFacets: (params) => api.get('/inventory/balances/summary/facets', { params }).then((r) => r.data),
  // `BalanceSort`/`StockUnitSort` are `Literal`s server-side, so a caller may
  // only send `updated_at`/`physical_quantity` and `code`/`created_at`.
  stockUnits: (params) => api.get('/inventory/stock-units', { params }).then((r) => r.data),
  stockUnit: (id) => api.get(`/inventory/stock-units/${id}`).then((r) => r.data),
  // Tem đơn vị tồn (2026-09-12). QR sinh ở server và về dưới dạng base64 trong
  // payload, không phải một endpoint ảnh: `<img src>` không gửi header
  // `Authorization` mà `api-client.js` gắn, nên ảnh nhúng thẳng sẽ 401.
  timeline: (id) => api.get(`/inventory/stock-units/${id}/timeline`).then((r) => r.data),
};

// design §7.6. A transfer *owns* documents rather than being one: each
// `create-*-document` command answers with an `inventory_documents` row that then
// runs the ordinary print → confirm lifecycle through `inventoryDocumentsApi`.
// The three commands are idempotency-keyed and version-checked for the same
// reason `confirm` is — each one posts stock.


// design §7.10. A phiên kiểm kê is not a chứng từ: it has its own lifecycle
// (Đang mở → Đang đếm → Đối chiếu → Đã đóng) and produces one, rather than being
// one. Two calls here answer with an `inventory_documents` row instead of a
// phiên — `createAdjustments` and `supersedeAdjustment` — and from that point
// the phiếu runs the ordinary print → ký → xác nhận lifecycle through
// `inventoryDocumentsApi`, same as a transfer's legs do.
export const stocktakesApi = {
  list: (params) => api.get('/stocktakes', { params }).then((r) => r.data),
  summary: (params) => api.get('/stocktakes/summary', { params }).then((r) => r.data),
  // Khu of one kho with the live phiên holding each, if any: the open drawer
  // greys a taken khu and names its phiên rather than hiding it.
  get: (id) => api.get(`/stocktakes/${id}`).then((r) => r.data),
  create: (data) => api.post('/stocktakes', data).then((r) => r.data),
  startCounting: (id, expectedVersion) => api.post(`/stocktakes/${id}/start-counting`, { expected_version: expectedVersion }).then((r) => r.data),
  // One đợt đếm — every row the operator typed, in one request. The server
  // freezes tồn hệ thống per row as it writes, so the client never sends an
  // expected quantity of its own.
  recordCounts: (id, counts) => api.post(`/stocktakes/${id}/counts`, { counts }).then((r) => r.data),
  // Every attempt at one object, newest first.
  counts: (id, params) => api.get(`/stocktakes/${id}/counts`, { params }).then((r) => r.data),
  // PATCH changes *which* attempt is trusted, never the attempt itself — counts
  // are append-only server-side.
  acceptCount: (id, measureId) => api.patch(`/stocktakes/${id}/counts/${measureId}`).then((r) => r.data),
  movementsDuringCount: (id) => api.get(`/stocktakes/${id}/movements-during-count`).then((r) => r.data),
  reconcile: (id, expectedVersion) => api.post(`/stocktakes/${id}/reconcile`, { expected_version: expectedVersion }).then((r) => r.data),
  createAdjustments: (id, idempotencyKey) => api.post(`/stocktakes/${id}/create-adjustments`, null, { headers: { 'Idempotency-Key': idempotencyKey } }).then((r) => r.data),
  supersedeAdjustment: (id, reason) => api.post(`/stocktakes/${id}/supersede-adjustment`, { reason }).then((r) => r.data),
  close: (id, expectedVersion) => api.post(`/stocktakes/${id}/close`, { expected_version: expectedVersion }).then((r) => r.data),
  cancel: (id, reason) => api.post(`/stocktakes/${id}/cancel`, { reason }).then((r) => r.data),
};
